import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthHeader, defaultAuthMenuItems } from '../auth/AuthHeader';
import { apiClient } from '../../api/httpClient';
import { zynity_api } from '../../config/external-routes';
import { getPasswordError } from './registrationUtils';
import '../auth/LoginPage.css';
import './PeopleRegistration.css';

const SUCCESS_MESSAGE =
  'Your registration was accepted. You can now login with the username and password you just entered.';

export const PeopleRegisterStep2Page: React.FC = () => {
  const location = useLocation();
  const [formValues, setFormValues] = useState({
    newPassword: '',
    newPasswordRepeat: '',
  });
  const [statusMessage, setStatusMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const id = queryParams.get('id') || '';
  const code = queryParams.get('code') || '';

  const handleChange =
    (field: keyof typeof formValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((current) => ({ ...current, [field]: event.target.value }));
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;

    const passwordError = getPasswordError(formValues.newPassword, formValues.newPasswordRepeat);
    if (passwordError) {
      setStatusMessage(passwordError);
      return;
    }

    if (!id || !code) {
      setStatusMessage('Missing registration details. Please use the link in your email.');
      return;
    }

    setIsSubmitting(true);
    setStatusMessage('');

    try {
      await apiClient.post(zynity_api.people_register_step2, {
        id,
        code,
        newPassword: formValues.newPassword,
        newPasswordRepeat: formValues.newPasswordRepeat,
      });
      const loginUrl = `/site/login?msg=${encodeURIComponent(SUCCESS_MESSAGE)}`;
      window.location.assign(loginUrl);
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to complete registration. Please try again.';
      setStatusMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-page">
      <AuthHeader menuItems={defaultAuthMenuItems} />

      <main className="registration-content">
        <h1 className="registration-title">New User Registration - Step 2</h1>
        <div className="registration-card">
          {statusMessage && (
            <p className="registration-message error">{statusMessage}</p>
          )}

          <div className="registration-intro">
            <p>To complete your user account at Zynity.com™ Enter a password of your choice submit.</p>
            <p>After submission you will be directed to login and manage your account.</p>
            <p>
              Need help? Click here &gt;&gt;&gt;{' '}
              <a href="/site/helpRequest">Help Request</a>
            </p>
          </div>

          <form className="registration-form" onSubmit={handleSubmit}>
            <div className="registration-grid">
              <div className="registration-field">
                <label htmlFor="newPassword">Password *</label>
                <input
                  id="newPassword"
                  type="password"
                  value={formValues.newPassword}
                  onChange={handleChange('newPassword')}
                  required
                />
                <span className="registration-helper">
                  Password can be a combination of letters, numbers and these symbols: ! @ # $ % ^ *
                </span>
              </div>
              <div className="registration-field">
                <label htmlFor="newPasswordRepeat">Confirm Password *</label>
                <input
                  id="newPasswordRepeat"
                  type="password"
                  value={formValues.newPasswordRepeat}
                  onChange={handleChange('newPasswordRepeat')}
                  required
                />
              </div>
            </div>
            <button type="submit" className="registration-submit" disabled={isSubmitting}>
              Submit
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};
